/**
 * URL Input Section
 *
 * Input field for product URL with extract button.
 */

import { useState } from 'react';
import { Link2, Loader2, Sparkles, Upload } from 'lucide-react';
import { Button, Input, Card, CardContent } from '@/components/v2/ui';

interface UrlInputSectionProps {
  onExtract: (url: string) => void;
  isLoading: boolean;
  error?: string | null;
  onManualUpload: () => void;
}

export function UrlInputSection({
  onExtract,
  isLoading,
  error,
  onManualUpload,
}: UrlInputSectionProps) {
  const [url, setUrl] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (url.trim()) {
      onExtract(url.trim());
    }
  };

  const isValidUrl = url.startsWith('http://') || url.startsWith('https://');

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-lg bg-primary-100 dark:bg-primary-900/30">
            <Link2 className="h-5 w-5 text-primary-600 dark:text-primary-400" />
          </div>
          <div>
            <h2 className="font-semibold">Produkt-URL eingeben</h2>
            <p className="text-sm text-neutral-500">
              Füge eine Shopware-Produkt-URL ein und lass KI deine Social-Media-Assets erstellen
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex gap-2">
            <Input
              type="url"
              placeholder="https://shop.example.com/produkt/..."
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className="flex-1"
              disabled={isLoading}
            />
            <Button
              type="submit"
              disabled={!url.trim() || !isValidUrl || isLoading}
              className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Extrahiere...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Generate Social Assets
                </>
              )}
            </Button>
          </div>

          {/* Error Message with Manual Upload Option */}
          {error && (
            <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <p className="text-sm font-medium text-red-800 dark:text-red-300">
                    {error}
                  </p>
                  {(error.includes('403') || error.includes('blockiert')) && (
                    <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                      Die Website blockiert automatische Anfragen. Verwende stattdessen den manuellen Upload.
                    </p>
                  )}
                </div>
                {(error.includes('403') || error.includes('blockiert')) && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={onManualUpload}
                    className="shrink-0"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Manuell hochladen
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Demo URLs */}
          <div className="text-sm text-neutral-500">
            <span className="mr-2">Demo-URLs zum Testen:</span>
            <div className="flex flex-wrap gap-2 mt-2">
              {[
                {
                  label: 'Bierdeckel',
                  url: 'https://funkeundbrand.de/products/bierdeckel-bier-party-50-stueck-untersetzer-fuer-geburtstag-gartenfeier-grillen-beer-bunter-bierdeckel-mit-einem-bier-in-der-mitte',
                },
                {
                  label: 'Soplica Likör',
                  url: 'https://soplica-vodka.com/products/soplica-haselnuss-lik-c3-b6r-orzech-laskowy-28-25-vol-500ml',
                },
                {
                  label: 'Tischkarten',
                  url: 'https://funkeundbrand.de/products/50-tischkarten-schoen-dass-du-da-bist-floral-butterfly-wildblumen',
                },
              ].map((demo) => (
                <button
                  key={demo.label}
                  type="button"
                  onClick={() => setUrl(demo.url)}
                  className="px-3 py-1 text-xs rounded-full bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-200 dark:hover:bg-neutral-700 transition-colors"
                >
                  {demo.label}
                </button>
              ))}
            </div>
          </div>

          {/* Manual Upload Alternative */}
          <div className="flex items-center gap-4 pt-4 border-t border-neutral-200 dark:border-neutral-700">
            <span className="text-sm text-neutral-500">
              Website blockiert? Produkt manuell hochladen:
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onManualUpload}
              disabled={isLoading}
            >
              <Upload className="h-4 w-4 mr-2" />
              Manueller Upload
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
